import { useState } from 'react';
import { Container } from '@/components/Container';
import { useTranslation } from '@/hooks/useTranslation';
import { getWhatsAppUrl } from '@/data/loader';

/* ═══════════════════════════════════════════
   FAQ GUÍA — Acordeão animado + categorias + Schema.org FAQPage
   ═══════════════════════════════════════════ */

interface FaqItem {
  q: string;
  a: string;
}

interface FaqCategoria {
  label: string;
  items: FaqItem[];
}

const faqCategorias: FaqCategoria[] = [
  {
    label: 'Chegar',
    items: [
      {
        q: 'Como chegar à Ilha do Mel?',
        a: 'O acesso é exclusivamente por barco. A travessia mais rápida sai de Pontal do Sul (30–35 min) e é a mais usada por quem vem de Curitiba. Outra opção é Paranaguá (~90 min de travessia pela baía — mais cenográfica).',
      },
      {
        q: 'Preciso reservar a balsa com antecedência?',
        a: 'O barco de Pontal do Sul para a Ilha do Mel não tem reserva — você compra a passagem no guichê no dia ou online. Na alta temporada (jan–mar, jul, feriados), chegue ao trapiche com pelo menos 1 hora de antecedência pois os horários lotam rápido.',
      },
      {
        q: 'Dá para ir de carro até a Ilha do Mel?',
        a: 'Não. A ilha não tem veículos motorizados. Você deixa o carro estacionado em Pontal do Sul (R$50–80/dia nos estacionamentos próximos ao trapiche) e cruza de barco.',
      },
      {
        q: 'Qual a diferença entre embarcar em Pontal do Sul e em Paranaguá?',
        a: 'Pontal do Sul é mais rápido (30–35 min de travessia) e mais próximo de Curitiba. Paranaguá leva ~90 minutos e é mais cenográfico, passando pela baía — boa opção para quem já está em Paranaguá ou quer a experiência paisagística.',
      },
      {
        q: 'Qual operadora de celular tem sinal na ilha?',
        a: 'Claro tem a melhor cobertura em Nova Brasília. Vivo funciona de forma intermitente. TIM e Oi têm cobertura muito limitada. Em Encantadas e nas trilhas, o sinal é instável em qualquer operadora. Baixe mapas e playlists offline antes de embarcar.',
      },
    ],
  },
  {
    label: 'Custos',
    items: [
      {
        q: 'Quanto custa a travessia de barco?',
        a: 'A passagem de Pontal do Sul varia entre R$18 e R$25 por trecho (valores sujeitos a reajuste). Crianças até 5 anos geralmente não pagam. Confirme o valor atualizado na Embarcações Ilha do Mel ou no site oficial.',
      },
      {
        q: 'Tem caixa eletrônico na Ilha do Mel?',
        a: 'Sim, há um ATM do Bradesco em Nova Brasília, mas com limite de saque baixo (~R$300–500 por transação). Em Encantadas não há banco nem ATM. Recomendamos sacar dinheiro antes de embarcar.',
      },
      {
        q: 'Os estabelecimentos aceitam cartão?',
        a: 'Pousadas geralmente aceitam débito e crédito. Muitos restaurantes em Nova Brasília também. Porém vendedores de praia, biroscas e passeios menores costumam ser cash-only — especialmente em Encantadas. Sempre confirme antes.',
      },
      {
        q: 'Quanto dinheiro em espécie devo levar?',
        a: 'Como referência, leve ao menos R$150–200 por pessoa por dia para cobrir refeições, lanches, passeios menores e imprevistos. Ajuste conforme seu perfil de consumo.',
      },
      {
        q: 'Os preços sobem na alta temporada?',
        a: 'Sim, significativamente. Acomodações e passeios podem custar 40–70% a mais em janeiro–março, julho e dezembro. Pousadas populares esgotam com meses de antecedência para Carnaval e Réveillon.',
      },
    ],
  },
  {
    label: 'Hospedagem',
    items: [
      {
        q: 'Onde se hospedar — Nova Brasília ou Encantadas?',
        a: 'Depende do seu perfil. Nova Brasília tem mais estrutura, opções de restaurantes e facilidades — ótima para famílias. Encantadas é mais tranquila e boêmia, com atmosfera especial e praias incríveis — ideal para casais e mochileiros.',
      },
      {
        q: 'Com quanto tempo de antecedência devo reservar a pousada?',
        a: 'Na alta temporada, reserve com 2–4 meses de antecedência (Carnaval e Réveillon: até 6 meses). Na baixa temporada, 2–4 semanas é suficiente e você pode conseguir melhores preços negociando diretamente com a pousada.',
      },
      {
        q: 'Posso levar pets para a Ilha do Mel?',
        a: 'Tecnicamente não há proibição formal na legislação federal, mas a ilha é APA (Área de Proteção Ambiental) e a grande maioria das pousadas não aceita animais. Confirme diretamente com a pousada antes de reservar.',
      },
      {
        q: 'Existe camping na ilha?',
        a: 'Sim, há opções de camping em áreas próximas à Praia de Fora e em Encantadas. O camping é uma opção econômica para mochileiros. Confirme localização e disponibilidade com a comunidade local ou grupos de viagem atualizados, pois as informações mudam por temporada.',
      },
    ],
  },
  {
    label: 'Na Ilha',
    items: [
      {
        q: 'Quantos dias são ideais para visitar?',
        a: 'Mínimo 2 noites para conhecer o básico de uma vila. Ideal 3–4 noites para explorar as duas vilas, fazer trilhas e um passeio de barco. Se quiser relaxar sem pressa e fazer as trilhas mais longas, 5–7 dias são perfeitos.',
      },
      {
        q: 'Tem hospital ou pronto-socorro na ilha?',
        a: 'Não há hospital. Existe um Posto de Saúde em Nova Brasília com atendimento básico em horário comercial, e outro em Encantadas com funcionamento reduzido. Para emergências graves, o paciente é removido por lancha ou helicóptero para Pontal do Sul ou Paranaguá. Leve seu kit de primeiros socorros.',
      },
      {
        q: 'Tem limite de visitantes por dia?',
        a: 'Sim, a ilha opera com cota de aproximadamente 5.000 visitantes simultâneos. Em Carnaval, Réveillon e feriados prolongados essa cota pode ser atingida, e novos embarques ficam impedidos. A solução é chegar cedo — no primeiro barco disponível.',
      },
      {
        q: 'Nova Brasília ou Encantadas — onde me hospedar?',
        a: 'Nova Brasília é mais estruturada, com mais opções de pousada, restaurantes e facilidades — ótima para famílias e primeira vez na ilha. Encantadas tem uma vibe mais tranquila e boêmia, com trilhas e praias incríveis — melhor para casais, mochileiros e quem já conhece NB.',
      },
    ],
  },
];

// Flatten all items for Schema.org (preserves original data integrity)
const allFaqItems: FaqItem[] = faqCategorias.flatMap(c => c.items);

export function FAQSectionGuia() {
  const { t } = useTranslation();
  const [openIdx, setOpenIdx] = useState<string | null>(null);

  const whatsappUrl = getWhatsAppUrl(
    '41999999999',
    'Oi! Tenho dúvidas sobre a Ilha do Mel e vi o guia no vemprailhadomel.com.br',
    'faq-duvidas'
  );

  return (
    <section id="faq" className="py-16 sm:py-20 bg-sand-50">
      <Container>
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
              {t('guide.faq.overline')}
            </span>
            <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
              {t('guide.faq.title')}
            </h2>
            <p className="font-editorial text-lg text-warm-gray mt-2">{t('guide.faq.subtitle')}</p>
          </div>

          {/* FAQ List por categorias */}
          <div className="space-y-8">
            {faqCategorias.map(categoria => {
              return (
                <div key={categoria.label}>
                  {/* Separador de categoria */}
                  <div className="mb-3">
                    <h3 className="font-body text-xs font-semibold text-teal-deep uppercase tracking-wider px-1">
                      {categoria.label}
                    </h3>
                  </div>

                  <div className="space-y-3">
                    {categoria.items.map((faq, itemIdx) => {
                      const idx = `${categoria.label}-${itemIdx}`;
                      const isOpen = openIdx === idx;

                      return (
                        <div
                          key={idx}
                          className="border border-sand-200 rounded-xl overflow-hidden"
                        >
                          <button
                            onClick={() => setOpenIdx(isOpen ? null : idx)}
                            aria-expanded={isOpen}
                            className="w-full flex items-start gap-4 p-5 text-left bg-white hover:bg-sand-50 transition-colors"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-teal-deep/10 text-teal-deep font-display text-sm flex items-center justify-center">
                              Q
                            </div>
                            <span className="font-body text-base font-semibold text-warm-dark flex-1 my-auto leading-snug">
                              {faq.q}
                            </span>
                            <svg
                              className={`w-5 h-5 text-warm-gray flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </button>

                          {/* Conteúdo animado com max-height */}
                          <div
                            className={`overflow-hidden transition-all duration-300 ease-in-out ${
                              isOpen ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
                            }`}
                          >
                            <div className="px-5 pb-5 pl-16 font-body text-sm text-warm-gray leading-relaxed">
                              {faq.a}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA WhatsApp */}
          <div className="mt-8 text-center">
            <p className="font-body text-sm text-warm-gray mb-3">Ainda tem dúvidas?</p>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20bd5a] text-white font-body font-semibold text-sm rounded-xl px-6 py-3 min-h-[44px] transition-all duration-200 hover:-translate-y-0.5"
            >
              <svg
                role="img"
                aria-label="WhatsApp"
                className="w-5 h-5"
                fill="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Fala com a gente no WhatsApp
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
}

/* Schema.org FAQPage JSON-LD */
export function FAQSchemaGuia() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: allFaqItems.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}
